import { CdkFixedSizeVirtualScroll, CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { DatePipe } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, effect, inject, input, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { AppService, sortUtils } from '@core';
import { ChildClient, ClientStatus } from '@models';
import { Store } from '@ngrx/store';
import { ClientManagementActions, ClientManagementSelectors } from '@ngrx-store';
import { CdkVirtualScrollViewportWrapperComponent, ConfirmationDialogComponent, NoDataPlaceholderComponent, SearchInputComponent, SkeletonTableComponent, VirtualTableScrollNormalizerDirective } from '@ui';

import { ClientStatusComponent } from '../client-status/client-status.component';

@Component({
  imports: [
    SkeletonTableComponent,
    MatSortHeader,
    NoDataPlaceholderComponent,
    MatButtonToggleGroup,
    MatButtonToggle,
    SearchInputComponent,
    MatSort,
    MatTable,
    MatHeaderCellDef,
    MatCellDef,
    MatHeaderCell,
    MatColumnDef,
    MatCell,
    ClientStatusComponent,
    MatTooltip,
    MatHeaderRow,
    MatRow,
    MatHeaderRowDef,
    MatRowDef,
    DatePipe,
    CdkFixedSizeVirtualScroll,
    CdkVirtualScrollViewport,
    VirtualTableScrollNormalizerDirective,
    CdkVirtualScrollViewportWrapperComponent,
  ],
  selector: 'avs-clients-table',
  styleUrl: './clients-table.component.scss',
  templateUrl: './clients-table.component.html',
})
export class ClientsTableComponent {
  tableId = input<string | null>(null);
  protected dataSource: MatTableDataSource<ChildClient> = new MatTableDataSource();
  protected clientStatusFilter = signal<'current' | 'archived'>('current');
  protected search = signal('');
  protected readonly eClientStatus = ClientStatus;
  protected readonly displayedColumns: string[] = ['index', 'friendlyName', 'status', 'created', 'actions'];

  private sort = viewChild(MatSort);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private store = inject(Store);
  private appService = inject(AppService);

  constructor() {
    this.store
      .select(ClientManagementSelectors.selectChildClients)
      .pipe(takeUntilDestroyed())
      .subscribe(clientList => {
        this.dataSource = new MatTableDataSource(clientList);
        this.dataSource.sortData = sortUtils.naturalSorting;
        this.dataSource.filterPredicate = this.filterLogic;
        this.filterTable(this.search(), this.clientStatusFilter());
      });

    effect(() => {
      this.filterTable(this.search(), this.clientStatusFilter());
    });

    effect(() => {
      const sortEl = this.sort();

      if (sortEl) {
        this.dataSource.sort = sortEl;
        this.cdr.detectChanges();
      }
    });
  }

  protected archiveClient(client: ChildClient) {
    this.openConfirmationDialog({
      clientId: client.id,
      data: { isArchived: true },
      description: `Do you really want to archive ${client.friendlyName} ?`,
      title: 'Archive client',
    });
  }

  protected unarchiveClient(client: ChildClient) {
    this.openConfirmationDialog({
      clientId: client.id,
      data: { isArchived: false },
      description: `Do you really want to unarchive ${client.friendlyName} ?`,
      title: 'Unarchive client',
    });
  }

  protected activateClient(client: ChildClient) {
    this.openConfirmationDialog({
      clientId: client.id,
      data: { isDisabled: false },
      description: `Do you really want to activate ${client.friendlyName} ?`,
      title: 'Activate client',
    });
  }

  protected deactivateClient(client: ChildClient) {
    this.openConfirmationDialog({
      clientId: client.id,
      data: { isDisabled: true },
      description: `Do you really want to deactivate ${client.friendlyName} ?`,
      title: 'Deactivate client',
    });
  }

  protected async selectClient(clientId: string) {
    this.store.dispatch(ClientManagementActions.clientIdChanged({ clientId }));
    await this.router.navigateByUrl(`/client-management/client-details/${clientId}`);
  }

  private openConfirmationDialog({
    clientId,
    title,
    description,
    data,
  }: {
    clientId: string;
    data: Partial<ChildClient>;
    description: string;
    title: string;
  }) {
    ConfirmationDialogComponent.open(this.dialog, {
      description,
      title,
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmation => {
        if (confirmation) {
          this.store.dispatch(
            ClientManagementActions.updateChildClient({
              clientClientId: clientId,
              clientData: data,
            }),
          );
        }
      });
  }

  private filterTable(clientTextFilter: string, clientStatusFilter: 'current' | 'archived') {
    this.dataSource.filter = JSON.stringify({
      clientStatusFilter,
      clientTextFilter,
    });
  }

  private filterLogic(record: ChildClient, filter: string) {
    const filterData = JSON.parse(filter);
    const statusMatch =
      filterData.clientStatusFilter === 'archived'
        ? record.status === ClientStatus.ARCHIVED
        : record.status !== ClientStatus.ARCHIVED;
    const nameMatch =
      !filterData.clientTextFilter || record.friendlyName.toLowerCase().includes(filterData.clientTextFilter.toLowerCase());

    return statusMatch && nameMatch;
  }
}
